import { LicenseBanner } from '../../desktop/LicenseBanner'
import { LogoutChoiceModal } from '../../desktop/LogoutChoiceModal'
import { desktopBridge } from '../../desktop/bridge'
import { IS_DESKTOP, IS_PORTAL } from '../../config/app-role'
import { PasswordInput } from '../ui/PasswordInput'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore, useCan } from '../../stores/auth.store'
import { RESOURCE_MAP } from '@pusula/shared'
import { useNotificationStore } from '../../stores/notification.store'
import { useAiActivityStore } from '../../stores/ai-activity.store'
import { useEffect, useCallback, useState, Suspense, lazy } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { toast } from 'sonner'
import {
  LayoutDashboard, PlusCircle, FileText, Users2, FolderKanban, Clock,
  BarChart3, TrendingUp, Receipt, Landmark, CreditCard, PieChart,
  Bell, Settings, UserCog, ArrowLeftRight, KeyRound, LogOut,
  PanelLeftClose, PanelLeftOpen, Sun, Moon, CalendarOff, Sparkles, Loader2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from './LanguageSwitcher'

// Yapay Zekâ asistanı paneli — ilk açılışta yüklenir
const AiPanel = lazy(() => import('../ai/AiPanel').then((m) => ({ default: m.AiPanel })))

// Nav görünürlüğü: rota → o sayfanın okuma + yazma yetki anahtarları.
// Biri bile açıksa öğe görünür; ikisi de kapalıysa (yetki yok) gizlenir.
const NAV_PERMISSION: Record<string, string[]> = {}
for (const section of RESOURCE_MAP ?? []) {
  for (const page of section.pages) {
    if (!page.route) continue
    const keys = [page.read, page.write].filter(Boolean) as string[]
    if (keys.length) NAV_PERMISSION[page.route] = keys
  }
}

// ─── Pusula Logo SVG ──────────────────────────────────────────────────────────

function PusulaLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 128 128" xmlns="http://www.w3.org/2000/svg" fill="none" className={className}>
      <defs>
        <linearGradient id="needle-n" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#4F46E5"/>
          <stop offset="100%" stopColor="#6366F1"/>
        </linearGradient>
        <linearGradient id="needle-s" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#A5B4FC"/>
          <stop offset="100%" stopColor="#C7D2FE"/>
        </linearGradient>
      </defs>
      <line x1="40" y1="20" x2="40" y2="108" stroke="#6366F1" strokeWidth="12" strokeLinecap="round"/>
      <circle cx="72" cy="48" r="28" stroke="#6366F1" strokeWidth="10" fill="none"/>
      <circle cx="72" cy="48" r="22" stroke="#6366F1" strokeWidth="0.75" opacity="0.25" fill="none"/>
      <line x1="72" y1="16" x2="72" y2="24" stroke="#6366F1" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="72" y1="72" x2="72" y2="80" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
      <line x1="96" y1="48" x2="104" y2="48" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
      <line x1="91" y1="27" x2="87" y2="31" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" opacity="0.2"/>
      <line x1="91" y1="69" x2="87" y2="65" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" opacity="0.2"/>
      <line x1="53" y1="27" x2="57" y2="31" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" opacity="0.2"/>
      <line x1="53" y1="69" x2="57" y2="65" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" opacity="0.2"/>
      <polygon points="72,28 77,47 72,43 67,47" fill="url(#needle-n)"/>
      <polygon points="72,68 77,49 72,53 67,49" fill="url(#needle-s)"/>
      <circle cx="72" cy="48" r="3.5" fill="#6366F1"/>
      <circle cx="72" cy="48" r="1.5" fill="#E0E7FF"/>
    </svg>
  )
}

// ─── Navigation Types ────────────────────────────────────────────────────────

interface NavItem {
  to: string
  labelKey: string
  icon: LucideIcon
  color: string      // Tailwind text color class for the icon
  roles: string[]
}

interface NavSection {
  id: string
  labelKey: string  // empty means no header
  roles: string[]
  items: NavItem[]
}

// ─── Navigation Config ───────────────────────────────────────────────────────

const NAV_SECTIONS: NavSection[] = [
  {
    id: 'main',
    labelKey: '',
    roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP', 'ACCOUNTANT'],
    items: [
      { to: '/dashboard', labelKey: 'items.dashboard', icon: LayoutDashboard, color: 'text-indigo-400', roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP', 'ACCOUNTANT'] },
      { to: '/data-entry', labelKey: 'items.dataEntry', icon: PlusCircle, color: 'text-violet-400', roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP', 'ACCOUNTANT'] },
      { to: '/ai', labelKey: 'items.ai', icon: Sparkles, color: 'text-primary', roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP', 'ACCOUNTANT'] },
    ],
  },
  {
    id: 'business',
    labelKey: 'sections.business',
    roles: ['TENANT_ADMIN', 'PM', 'CR'],
    items: [
      { to: '/proposals', labelKey: 'items.proposals', icon: FileText, color: 'text-cyan-400', roles: ['TENANT_ADMIN', 'PM', 'CR'] },
      { to: '/customers', labelKey: 'items.customers', icon: Users2, color: 'text-teal-400', roles: ['TENANT_ADMIN', 'PM', 'CR'] },
    ],
  },
  {
    id: 'project',
    labelKey: 'sections.project',
    roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP'],
    items: [
      { to: '/projects', labelKey: 'items.projects', icon: FolderKanban, color: 'text-blue-400', roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP'] },
      { to: '/timesheets', labelKey: 'items.timesheets', icon: Clock, color: 'text-sky-400', roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP'] },
      { to: '/leave', labelKey: 'items.leave', icon: CalendarOff, color: 'text-violet-400', roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP', 'ACCOUNTANT'] },
    ],
  },
  {
    id: 'finance',
    labelKey: 'sections.finance',
    roles: ['TENANT_ADMIN', 'ACCOUNTANT', 'PM', 'MGMT'],
    items: [
      { to: '/finance/overview', labelKey: 'items.financeOverview', icon: BarChart3, color: 'text-emerald-400', roles: ['TENANT_ADMIN', 'ACCOUNTANT', 'PM', 'MGMT'] },
      { to: '/revenue', labelKey: 'items.revenue', icon: TrendingUp, color: 'text-green-400', roles: ['TENANT_ADMIN', 'ACCOUNTANT', 'PM', 'MGMT'] },
      { to: '/finance', labelKey: 'items.expenses', icon: Receipt, color: 'text-orange-400', roles: ['TENANT_ADMIN', 'ACCOUNTANT'] },
      { to: '/sub-consultants', labelKey: 'items.subConsultants', icon: Landmark, color: 'text-amber-400', roles: ['TENANT_ADMIN', 'PM', 'ACCOUNTANT'] },
      { to: '/instruments', labelKey: 'items.instruments', icon: CreditCard, color: 'text-yellow-400', roles: ['TENANT_ADMIN', 'ACCOUNTANT', 'PM', 'MGMT'] },
    ],
  },
  {
    id: 'other',
    labelKey: 'sections.other',
    roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP', 'ACCOUNTANT'],
    items: [
      { to: '/reports', labelKey: 'items.reports', icon: PieChart, color: 'text-pink-400', roles: ['TENANT_ADMIN', 'PM', 'MGMT', 'ACCOUNTANT'] },
      { to: '/notifications', labelKey: 'items.notifications', icon: Bell, color: 'text-rose-400', roles: ['TENANT_ADMIN', 'PM', 'TL', 'CR', 'MGMT', 'EMP', 'ACCOUNTANT'] },
    ],
  },
  {
    id: 'admin',
    labelKey: 'sections.admin',
    roles: ['TENANT_ADMIN'],
    items: [
      { to: '/users', labelKey: 'items.users', icon: UserCog, color: 'text-slate-400', roles: ['TENANT_ADMIN'] },
      { to: '/settings', labelKey: 'items.settings', icon: Settings, color: 'text-zinc-400', roles: ['TENANT_ADMIN'] },
    ],
  },
]

// ─── Theme Hook ──────────────────────────────────────────────────────────────

function useTheme() {
  const [isLight, setIsLight] = useState(() => {
    return document.documentElement.classList.contains('light')
  })

  const toggle = useCallback(() => {
    const next = !isLight
    setIsLight(next)
    if (next) {
      document.documentElement.classList.add('light')
    } else {
      document.documentElement.classList.remove('light')
    }
    try { localStorage.setItem('pusula-theme', next ? 'light' : 'dark') } catch { /* noop */ }
  }, [isLight])

  return { isLight, toggle }
}

// Init theme from storage before React renders (prevents flash)
;(function initTheme() {
  try {
    if (localStorage.getItem('pusula-theme') === 'light') {
      document.documentElement.classList.add('light')
    }
  } catch { /* noop */ }
})()

// ─── Component ───────────────────────────────────────────────────────────────

export function AppLayout(): JSX.Element {
  const { t } = useTranslation('nav')
  const { user, logout } = useAuthStore()
  const can = useCan()
  const queryClient = useQueryClient()
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showSwitchTenant, setShowSwitchTenant] = useState(false)
  const [logoutChoice, setLogoutChoice] = useState<{ clients: number } | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const { isLight, toggle: toggleTheme } = useTheme()
  const { unreadCount, fetch: fetchNotifications, subscribeSocket } = useNotificationStore()
  const aiRunningId = useAiActivityStore((s) => s.runningConversationId)
  const navigate = useNavigate()
  const location = useLocation()

  const { data: financeSettingsData } = useQuery({
    queryKey: ['finance-settings'],
    queryFn: () => api.get('/finance/settings').then(r => r.data as { data: { accountantCanViewReports: boolean } }),
    enabled: user?.role === 'ACCOUNTANT',
  })
  const accountantCanViewReports = financeSettingsData?.data?.accountantCanViewReports ?? false

  useEffect(() => {
    void fetchNotifications()
    subscribeSocket()
  }, [fetchNotifications, subscribeSocket])

  // Nav görünürlüğü TÜM roller için yetki-tabanlı: sayfanın okuma (yoksa yazma) yetkisi
  // yoksa o öğe — ve tüm öğeleri gizlenen bölüm — sol panelde görünmez.
  const isItemVisible = useCallback((item: NavItem): boolean => {
    if (!user) return false
    // Raporlar: ACCOUNTANT ayrıca accountantCanViewReports ayarıyla da görebilir.
    if (item.to === '/reports') {
      return can('REPORT_VIEW') || (user.role === 'ACCOUNTANT' && accountantCanViewReports)
    }
    const perms = NAV_PERMISSION[item.to]
    if (!perms) return true // Anasayfa, Hızlı Veri Girişi — herkese açık (yetki anahtarı yok)
    return perms.some((p) => can(p))
  }, [user, accountantCanViewReports, can])

  const isSectionVisible = useCallback((section: NavSection): boolean => {
    if (!user) return false
    return section.items.some(isItemVisible) // görünür alt öğesi yoksa bölüm gizlenir
  }, [user, isItemVisible])

  const isNavActive = useCallback((to: string): boolean => {
    const path = location.pathname
    if (to === '/finance') {
      return path === '/finance' || (path.startsWith('/finance/employees') || path.startsWith('/finance/payroll'))
    }
    return path.startsWith(to)
  }, [location.pathname])

  const finishLogout = async (closeDataCenter: boolean) => {
    setLogoutChoice(null)
    await logout()
    queryClient.clear()
    // Veri Merkezi kapanırken ana süreç pencereyi karşılama ekranına taşır; /login'e gidilmez
    if (closeDataCenter) await desktopBridge()?.switchDataCenter()
    else navigate('/login')
  }

  const handleLogout = async () => {
    // Ana makinede ve Veri Merkezi'ni yönetebilen kullanıcıya "Veri Merkezi de kapatılsın mı?" sorulur
    const bridge = desktopBridge()
    if (bridge && can('DATACENTER_MANAGE') && (await bridge.getInfo().then((i) => i.isHost).catch(() => false))) {
      const clients = await api.get<{ data: { connected: { remoteDevices: number } } }>('/desktop/status')
        .then((r) => r.data.data.connected.remoteDevices).catch(() => 0)
      setLogoutChoice({ clients })
      return
    }
    await finishLogout(false)
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-[68px]' : 'w-60'} flex-shrink-0 bg-sidebar border-r border-sidebar-border flex flex-col transition-all duration-300`}>
        {/* Logo + Collapse toggle */}
        <div className={`${collapsed ? 'px-2' : 'px-4'} py-4 border-b border-sidebar-border`}>
          {collapsed ? (
            /* Collapsed: logo and toggle stacked vertically */
            <div className="flex flex-col items-center gap-2">
              <PusulaLogo className="w-8 h-8" />
              <button
                onClick={() => setCollapsed(false)}
                className="text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-lg p-1.5 transition-all duration-150"
                title={t('user.expandMenu')}
              >
                <PanelLeftOpen size={18} />
              </button>
            </div>
          ) : (
            /* Expanded: logo left, toggle right */
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5 min-w-0">
                <PusulaLogo className="w-8 h-8 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-foreground leading-none tracking-tight">Pusula</p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[120px]">{user?.tenant?.name}</p>
                </div>
              </div>
              <button
                onClick={() => setCollapsed(true)}
                className="text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-lg p-1.5 transition-all duration-150"
                title={t('user.collapseMenu')}
              >
                <PanelLeftClose size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-3'} py-3 overflow-y-auto`}>
          {NAV_SECTIONS.map(section => {
            if (!isSectionVisible(section)) return null
            const visibleItems = section.items.filter(isItemVisible)
            if (visibleItems.length === 0) return null

            return (
              <div key={section.id} className={section.labelKey ? 'mt-4 first:mt-0' : ''}>
                {section.labelKey && !collapsed && (
                  <div className="px-3 mb-1.5">
                    <span className="text-xs font-semibold tracking-wider text-muted-foreground/50 uppercase">
                      {t(section.labelKey)}
                    </span>
                  </div>
                )}
                {section.labelKey && collapsed && (
                  <div className="mx-2 mb-1.5 border-t border-sidebar-border" />
                )}

                <div className="space-y-0.5">
                  {visibleItems.map(item => {
                    const Icon = item.icon
                    const active = isNavActive(item.to)
                    const itemLabel = t(item.labelKey)
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to === '/ai' && aiRunningId ? `/ai?c=${aiRunningId}` : item.to}
                        end={item.to === '/finance'}
                        title={collapsed ? itemLabel : undefined}
                        className={() =>
                          `flex items-center ${collapsed ? 'justify-center' : ''} gap-3 rounded-lg ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2'} text-sm transition-all duration-150 relative ${
                            active
                              ? 'bg-primary/15 text-primary font-medium'
                              : 'text-sidebar-foreground hover:bg-accent/50 hover:text-foreground'
                          }`
                        }
                      >
                        <Icon
                          size={collapsed ? 22 : 18}
                          className={`flex-shrink-0 transition-all duration-150 ${active ? 'text-primary' : item.color}`}
                        />
                        {!collapsed && <span className="flex-1 truncate">{itemLabel}</span>}
                        {item.to === '/ai' && aiRunningId && (
                          <Loader2 size={collapsed ? 12 : 14} className={`animate-spin text-primary ${collapsed ? 'absolute -top-0.5 -right-0.5' : ''}`} aria-label={t('aiRunning')} />
                        )}
                        {item.to === '/notifications' && unreadCount > 0 && (
                          <span className={`bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center ${
                            collapsed ? 'absolute -top-0.5 -right-0.5 w-4 h-4 text-[10px]' : 'w-5 h-5'
                          }`}>
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </span>
                        )}
                      </NavLink>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </nav>

        {/* Bottom — User */}
        <div className={`${collapsed ? 'px-2' : 'px-3'} py-3 border-t border-sidebar-border space-y-0.5`}>
          {/* User card */}
          {!collapsed && (
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold flex-shrink-0">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-foreground truncate">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-muted-foreground">{user?.role ? t(`roles.${user.role}`, { defaultValue: user.role }) : ''}</p>
              </div>
            </div>
          )}

          {collapsed && (
            <div className="flex justify-center py-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold" title={`${user?.firstName} ${user?.lastName}`}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
            </div>
          )}

          <button
            onClick={() => setShowSwitchTenant(true)}
            title={collapsed ? t('user.switchTenant') : undefined}
            className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 rounded-lg ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2'} text-sm text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-all duration-150`}
          >
            <ArrowLeftRight size={collapsed ? 22 : 18} className="flex-shrink-0 text-cyan-400 transition-all duration-150" />
            {!collapsed && <span>{t('user.switchTenant')}</span>}
          </button>

          <button
            onClick={() => setShowChangePassword(true)}
            title={collapsed ? t('user.changePassword') : undefined}
            className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 rounded-lg ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2'} text-sm text-muted-foreground hover:bg-accent/50 hover:text-foreground transition-all duration-150`}
          >
            <KeyRound size={collapsed ? 22 : 18} className="flex-shrink-0 text-violet-400 transition-all duration-150" />
            {!collapsed && <span>{t('user.changePassword')}</span>}
          </button>

          {/* Language switcher */}
          <LanguageSwitcher collapsed={collapsed} />

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            title={isLight ? t('common:theme.switchToDark', { defaultValue: 'Karanlık temaya geç' }) : t('common:theme.switchToLight', { defaultValue: 'Açık temaya geç' })}
            className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 rounded-lg ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2'} text-sm text-sidebar-foreground hover:bg-accent/50 hover:text-foreground transition-all duration-150`}
          >
            {isLight
              ? <Moon size={collapsed ? 22 : 18} className="flex-shrink-0 text-indigo-400 transition-all duration-150" />
              : <Sun size={collapsed ? 22 : 18} className="flex-shrink-0 text-amber-400 transition-all duration-150" />
            }
            {!collapsed && <span>{isLight ? t('common:theme.dark', { defaultValue: 'Karanlık Tema' }) : t('common:theme.light', { defaultValue: 'Açık Tema' })}</span>}
          </button>

          <button
            onClick={() => void handleLogout()}
            title={collapsed ? t('user.logout') : undefined}
            className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 rounded-lg ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2'} text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all duration-150`}
          >
            <LogOut size={collapsed ? 22 : 18} className="flex-shrink-0 text-rose-400 transition-all duration-150" />
            {!collapsed && <span>{t('user.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-auto">
        {/* Masaüstü lisans bandı (salt-okunur / çevrimdışı tolerans) */}
        {IS_DESKTOP && <LicenseBanner />}
        <Suspense fallback={
          <div className="flex items-center justify-center py-24">
            <div className="h-6 w-6 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          </div>
        }>
          <Outlet />
        </Suspense>
      </main>

      {/* Yapay Zekâ asistanı — masaüstünde, AI_USE yetkisiyle */}
      {!IS_PORTAL && can('AI_USE') && <Suspense fallback={null}><AiPanel /></Suspense>}

      {/* Modals */}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} onSuccess={() => { setShowChangePassword(false); void handleLogout() }} />
      )}

      {showSwitchTenant && (
        <SwitchTenantModal onClose={() => setShowSwitchTenant(false)} />
      )}

      {logoutChoice && (
        <LogoutChoiceModal
          connectedClients={logoutChoice.clients}
          onLogoutOnly={() => void finishLogout(false)}
          onCloseCenter={() => void finishLogout(true)}
          onCancel={() => setLogoutChoice(null)}
        />
      )}
    </div>
  )
}

// ─── Şifre Değiştir Modal ─────────────────────────────────────────────────────

function ChangePasswordModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { t } = useTranslation(['nav', 'common'])
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (newPassword.length < 8) {
      setError(t('changePassword.minError'))
      return
    }
    if (newPassword !== confirmPassword) {
      setError(t('changePassword.mismatch'))
      return
    }

    setLoading(true)
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword, confirmPassword })
      onSuccess()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
      setError(msg ?? t('changePassword.failed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-card rounded-xl shadow-xl w-full max-w-md mx-4 border border-border/50">
        <div className="px-6 py-4 border-b border-border/50 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">{t('changePassword.title')}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl leading-none">&times;</button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
          )}

          <div>
            <label className="label">{t('changePassword.current')}</label>
            <PasswordInput
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="label">{t('changePassword.new')}</label>
            <PasswordInput
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
            <p className="text-xs text-muted-foreground mt-1">{t('changePassword.minHint')}</p>
          </div>

          <div>
            <label className="label">{t('changePassword.confirm')}</label>
            <PasswordInput
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common:actions.cancel')}
            </button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? t('changePassword.submitting') : t('changePassword.submit')}
            </button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            {t('changePassword.loginNotice')}
          </p>
        </form>
      </div>
    </div>
  )
}

// ─── Kuruluş Değiştir Modal ───────────────────────────────────────────────────

interface TenantMembership {
  tenantId: string
  tenantName: string
  subdomain: string
  plan: string
  role: string
  isCurrent: boolean
}

function SwitchTenantModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['nav', 'common'])
  const { switchTenant, isLoading } = useAuthStore()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading: fetching } = useQuery<{ data: TenantMembership[] }>({
    queryKey: ['my-tenants'],
    queryFn: () => api.get('/auth/my-tenants').then(r => r.data),
  })

  const tenants = data?.data ?? []

  const handleSwitch = async (tenantId: string) => {
    try {
      await switchTenant(tenantId)
      queryClient.clear()
      navigate('/dashboard')
      onClose()
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
      toast.error(msg ?? t('tenant.switchError'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-card rounded-xl shadow-xl w-full max-w-sm mx-4 border border-border/50">
        <div className="px-6 py-4 border-b border-border/50 flex items-center justify-between">
          <h2 className="text-base font-semibold text-foreground">{t('tenant.switchTitle')}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">&times;</button>
        </div>

        <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
          {fetching ? (
            <p className="text-center text-sm text-muted-foreground py-4">{t('common:states.loading')}</p>
          ) : tenants.length <= 1 ? (
            <p className="text-center text-sm text-muted-foreground py-4">
              {t('tenant.onlyOne')}
            </p>
          ) : (
            tenants.map(tn => (
              <button
                key={tn.tenantId}
                onClick={() => { if (!tn.isCurrent) void handleSwitch(tn.tenantId) }}
                disabled={tn.isCurrent || isLoading}
                className={`w-full text-left rounded-lg px-4 py-3 border transition-colors ${
                  tn.isCurrent
                    ? 'border-primary bg-primary/10 cursor-default'
                    : 'border-border/50 hover:bg-accent/50 cursor-pointer'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">{tn.tenantName}</p>
                  {tn.isCurrent && <span className="text-xs text-primary font-semibold uppercase">{t('tenant.active')}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t(`rolesShort.${tn.role}`, { defaultValue: tn.role })} · {t(`plans.${tn.plan}`, { defaultValue: tn.plan })}
                </p>
              </button>
            ))
          )}
        </div>

        <div className="px-6 py-4 border-t border-border/50 flex justify-end">
          <button onClick={onClose} className="btn-secondary">{t('common:actions.close')}</button>
        </div>
      </div>
    </div>
  )
}
